import { useMutation } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FacilityPicker } from "components/FacilityPicker";
import {
  RegistrationFormFields,
  RegistrationFormValues,
} from "components/waste/registration/RegistrationForm";
import { StateWasteStreamRegistrationStatus } from "generated-graphql/graphql";
import { Controller, useForm } from "react-hook-form";

import { CREATE_STATE_WASTE_STREAM_REGISTRATION } from "./api";

// A manually-created registration defaults to In Progress: with the state ID
// left blank this is the in-progress placeholder the dialog description promises
// (blank id + In Progress), and it's the right starting point even when an id is
// supplied — the user can flip it to Registered.
const DEFAULT_VALUES: RegistrationFormValues = {
  facility: null,
  stateRegisteredId: "",
  profileNumber: "",
  status: StateWasteStreamRegistrationStatus.InProgress,
  name: "",
  companyDescription: "",
  federalWasteCodes: [],
  formCodes: [],
  sourceCode: "",
  dotId: "",
  dotDescription: "",
  registeredAt: null,
  closedAt: null,
};

type Props = {
  open: boolean;
  onClose: () => void;
  // The page's selected tenant. Scopes the facility picker so a staff user can
  // only create a registration for a facility in this tenant — otherwise the
  // new row would belong to another org and vanish from the tenant-scoped grid.
  tenantId: string;
};

/**
 * Staff-only creation dialog for a manually-entered state waste stream
 * registration. The state comes from the chosen facility (a facility is in one
 * state), and `stateRegisteredId` is optional — leaving it blank creates an
 * in-progress placeholder ahead of the state assigning an identifier. The
 * fields below the facility picker are shared with the edit drawer via
 * RegistrationFormFields.
 */
export function CreateRegistrationDialog({ open, onClose, tenantId }: Props) {
  const alerts = useAlerts();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm<RegistrationFormValues>({ defaultValues: DEFAULT_VALUES });

  const [createRegistration, { loading }] = useMutation(CREATE_STATE_WASTE_STREAM_REGISTRATION, {
    refetchQueries: ["StateWasteStreamRegistrations"],
    awaitRefetchQueries: true,
  });

  const facility = watch("facility");
  const facilityState = facility?.state ?? "";

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  const onSubmit = async (data: RegistrationFormValues) => {
    if (!data.facility?.state) return;
    try {
      await createRegistration({
        variables: {
          input: {
            facilityId: data.facility.id,
            state: data.facility.state,
            stateRegisteredId: data.stateRegisteredId.trim() || undefined,
            profileNumber: data.profileNumber.trim() || undefined,
            status: data.status,
            registeredAt: data.registeredAt || undefined,
            closedAt: data.closedAt || undefined,
            federalWasteCodes: data.federalWasteCodes,
            formCodes: data.formCodes,
            sourceCode: data.sourceCode.trim() || undefined,
            dotId: data.dotId.trim() || undefined,
            dotDescription: data.dotDescription.trim() || undefined,
            name: data.name.trim() || undefined,
            companyDescription: data.companyDescription.trim() || undefined,
          },
        },
      });
      alerts.success("Registration created.");
      reset(DEFAULT_VALUES);
      onClose();
    } catch (e) {
      alerts.error("Failed to create the registration.", e);
    }
  };

  const busy = loading || isSubmitting;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 560, maxWidth: "90vw" } } }}
      title="Create state waste stream registration"
      description="Leave State Registered ID blank to create an in-progress placeholder ahead of the state assigning an identifier."
      primaryActionButtonProps={{
        onClick: handleSubmit(onSubmit),
        loading: busy,
        loadingText: "Creating...",
        hideStartIcon: true,
        saveText: "Create registration",
        disabled: busy || !facility?.state,
      }}
      cancelButtonProps={{ disabled: busy }}
    >
      <Stack spacing={2}>
        <Controller
          name="facility"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <FacilityPicker
              {...fieldState}
              label="Facility"
              facilityQueryVariant="waste"
              defaultSearchTerm={tenantId ? `tenantId:${tenantId}` : undefined}
              value={field.value}
              onChange={field.onChange}
              disabled={busy}
            />
          )}
        />
        {facility && (
          <Typography variant="caption" color="text.secondary">
            State: {facilityState || "— (facility has no state on file)"}
          </Typography>
        )}
        <RegistrationFormFields control={control} disabled={busy} />
      </Stack>
    </Dialog>
  );
}
